import { isNull } from "../../util/TypeUtil";

/**
 * Calculates simplified drag inertia physics based on coordinates updates as a given elapsed time.
 * 
 * This class maintains the coordinate speeds and apply inertia when it is activated. Thus, decelerating
 * the speed based on a given rate.
 * 
 * @author Henrique Campos for &ranj
 */
export default class DragInertia {
    constructor(decelerationRate = 0.135) {
        this._decelerationRate = decelerationRate;
        this._x = this._y = this._previousX = this._previousY = undefined;
        this._speedX = 0;
        this._speedY = 0;
        this._isInertiaActive = false;
    }

    /**
     * Starts inertia physics calculation
     */
    startInertia() {
        this._isInertiaActive = true;
    }

    /**
     * Stops inertia physics calculations and resets its values
     */
    stopInertia() {
        if (!this._isInertiaActive) {
            return;
        }
        this._speedX = 0;
        this._speedY = 0;
        this._isInertiaActive = false;
    }

    /** @returns {boolean} True if inertia physics are active. False, otherwise. */
    get isInertiaActive() {
        return this._isInertiaActive;
    }

    /** @returns {number} The deceleration rate for the inertia */
    get decelerationRate() {
        return this._decelerationRate;
    }

    /** @param {number} value The new deceleration value*/
    set decelerationRate(value) {
        this._decelerationRate = value;
    }

    /** @returns {number} The calculated x coordinate affected by inertia physics */
    get x() {
        return this._x;
    }

    /** @returns {number} The calculated x coordinate affected by inertia physics */
    get y() {
        return this._y;
    }

    /**
     * Updates inertia physics and applies to x, y coordinates if inertia is active
     * 
     * @param {number} x x coordinate
     * @param {number} y y coordinate
     * @param {number} deltaSeconds elapsed seconds
     */
    update(x, y, deltaSeconds) {
        // initialize for first update
        if (isNull(this._x)) {
            this._x = this._previousX = x;
        }
        if (isNull(this._y)) {
            this._y = this._previousY = y;
        }

        if (this._isInertiaActive) {
            // If inertia is active apply the speed to the coordinates
            this._x += this._speedX * deltaSeconds;
            this._y += this._speedY * deltaSeconds;
        } else {
            // Otherwise, just set the coordinates for the sake of calculating its inherit speed
            this._x = x;
            this._y = y;
        }

        // determine speed based on previous coordinates
        this._speedX = (this._x - this._previousX) / deltaSeconds;
        this._speedY = (this._y - this._previousY) / deltaSeconds;
        this._previousX = this._x;
        this._previousY = this._y;

        if (this._isInertiaActive) {
            // calculate deceleration of speeds
            this._speedX = this._calculateSpeedDeceleration(this._speedX, this._decelerationRate, deltaSeconds);
            this._speedY = this._calculateSpeedDeceleration(this._speedY, this._decelerationRate, deltaSeconds);

            // determine if inertia should stop based on speed
            if (this._speedX === 0 && this._speedY === 0) {
                this._isInertiaActive = false;
            }
        }
    }

    _calculateSpeedDeceleration(speed, decelerationRate, deltaSeconds) {
        // based on: https://github.com/liuqiaosz/Unity/blob/master/UGUI%E6%BA%90%E4%BB%A3%E7%A0%81/UnityEngine.UI/UI/Core/ScrollRect.cs#L311
        let newSpeed = decelerationRate <= 0 ? 0 : speed * Math.pow(decelerationRate, deltaSeconds);
        if (Math.abs(newSpeed) < 1) {
            newSpeed = 0;
        }
        return newSpeed;
    }
}